import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Calendar, ArrowLeft } from "lucide-react";
import axios from "axios";
import { PUBLIC_API } from "../lib/api";
import TeroumaLogo from "../components/ui/TeroumaLogo";

export default function KiddoushConfirmPage() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | success | expired | error
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${PUBLIC_API}/kiddoush/confirm/${token}`)
      .then(r => { setData(r.data); setStatus("success"); })
      .catch(err => {
        const code = err?.response?.status;
        if (code === 410) setStatus("expired");
        else if (code === 404) setStatus("expired");
        else setStatus("error");
      });
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr + "T12:00:00").toLocaleDateString("fr-FR", {
        weekday: "long", day: "2-digit", month: "long", year: "numeric"
      });
    } catch { return dateStr; }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#F4F3EF" }}>
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-md mx-auto px-6 h-16 flex items-center">
          <Link to="/"><TeroumaLogo textColor="#0D1B33" /></Link>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-10 max-w-md w-full text-center">

          {status === "loading" && (
            <>
              <Loader2 className="h-10 w-10 animate-spin text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">Validation en cours…</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-semibold text-stone-900 mb-2">
                {data?.already_confirmed ? "Déjà confirmé !" : "Demande confirmée !"}
              </h1>
              <p className="text-stone-500 text-sm mb-5">
                {data?.already_confirmed
                  ? <>Votre demande de Kiddoush au <strong className="text-stone-700">{data?.org_name}</strong> est déjà enregistrée.</>
                  : <>Votre demande de Kiddoush au <strong className="text-stone-700">{data?.org_name}</strong> a bien été confirmée et transmise à l'équipe.</>
                }
              </p>
              {data?.desired_date && (
                <div className="flex items-center justify-center gap-2 bg-stone-50 rounded-xl px-4 py-3 mb-6 text-sm text-stone-700">
                  <Calendar className="h-4 w-4" style={{ color: "#B8962E" }} />
                  <span className="capitalize">{formatDate(data.desired_date)}</span>
                </div>
              )}
              <p className="text-xs text-stone-400 mb-6">
                {data?.already_confirmed
                  ? "L'équipe reviendra vers vous pour la confirmation définitive."
                  : "L'équipe va maintenant examiner votre demande et vous contacter pour la confirmation définitive."}
              </p>
              <Link to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-white text-sm font-medium"
                style={{ background: "#0D1B33" }}>
                <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
              </Link>
            </>
          )}

          {(status === "expired" || status === "error") && (
            <>
              <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
                <XCircle className="h-8 w-8 text-red-400" />
              </div>
              <h1 className="text-2xl font-semibold text-stone-900 mb-2">
                {status === "expired" ? "Lien expiré" : "Lien invalide"}
              </h1>
              <p className="text-stone-500 text-sm mb-6">
                {status === "expired"
                  ? "Ce lien de confirmation est expiré ou a déjà été utilisé. Votre demande a été annulée. Vous pouvez faire une nouvelle demande de Kiddoush."
                  : "Ce lien de confirmation n'est pas valide. Il a peut-être déjà été utilisé."}
              </p>
              <Link to="/kiddoush"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-white text-sm font-medium"
                style={{ background: "#0D1B33" }}>
                Faire une nouvelle demande
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
